import { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { LayoutDashboard, LogOut, Building2, ClipboardList, FileText, MessageSquare } from 'lucide-react';

export const ClientLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [loggingOut, setLoggingOut] = useState(false);

  const handleLogout = () => {
    setLoggingOut(true);
    logout();
    navigate('/login');
  };

  const navItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/client' },
    { icon: ClipboardList, label: 'Staffing Requests', path: '/client/requests' },
    { icon: FileText, label: 'Documents', path: '/client/documents' },
    { icon: MessageSquare, label: 'Messages', path: '/client/messages' },
  ];

  return (
    <div className="min-h-screen bg-[#eef0f3] flex flex-col">
      {/* Dark brand header */}
      <header className="bg-[#14181f] text-white px-4 py-3 sm:py-4">
        <div className="max-w-5xl mx-auto flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <img src="/san-logo.jpg" alt="S.A.N Security" className="w-10 h-10 sm:w-12 sm:h-12 object-cover rounded-sm shrink-0" />
            <div className="min-w-0">
              <p className="font-barlow tracking-widest text-base sm:text-xl leading-none truncate">
                S.A.N SECURITY <span className="text-primary">CLIENT PORTAL</span>
              </p>
              <p className="text-[10px] sm:text-xs text-white/60 mt-0.5 flex items-center gap-1.5 truncate">
                <Building2 className="w-3 h-3 shrink-0" />
                {user?.company_name || user?.name}
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLogout}
            disabled={loggingOut}
            data-testid="client-logout-btn"
            className="text-white/70 hover:text-white hover:bg-white/10 rounded-sm shrink-0"
          >
            <LogOut className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Log out</span>
          </Button>
        </div>
      </header>

      {/* Nav */}
      <nav className="bg-white border-b border-zinc-200">
        <div className="max-w-5xl mx-auto px-4 flex gap-1">
          {navItems.map((item) => {
            const active = item.path === '/client' ? location.pathname === '/client' : location.pathname.startsWith(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`client-nav-${item.label.toLowerCase()}`}
                className={`flex items-center gap-2 px-3 sm:px-4 py-2.5 text-xs sm:text-sm font-medium border-b-2 transition-colors ${
                  active
                    ? 'border-primary text-zinc-900'
                    : 'border-transparent text-zinc-500 hover:text-zinc-900'
                }`}
              >
                <item.icon className="w-4 h-4" strokeWidth={1.75} />
                {item.label}
              </Link>
            );
          })}
        </div>
      </nav>

      <main className="flex-1 w-full max-w-5xl mx-auto px-3 sm:px-6 py-4 sm:py-8">{children}</main>

      <footer className="bg-[#14181f] text-white px-4 py-2.5 text-center">
        <p className="text-[10px] sm:text-xs text-white/50">S.A.N Security Ltd · Your Safety. Our Priority.</p>
      </footer>
    </div>
  );
};

export default ClientLayout;
